// GCR TODO: Rewrite using SVGInteraction class

// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## ProtoNotation
// Generates an SVG with some animateable properties and a proto-pitch
// notation graph, showing higher/lower/same pitches, etc...
//
// @param points: array of integers (range 0-100) indicating the relative
//   height of the notes
// @param width: width of SVG (default: 210)
// @param height: height of SVG (default: 140)
import Mousetrap from 'mousetrap';
import { getCssProperty } from '../uTheory.Interaction/getCssProperty';
import SVGInteraction from './SVG';
import utils from '../utils';
import config from '../config';

// Default Values:
let width = 210;
let height = 140;
let strokeWidth = 5;
let radius = 10;

const heightDescriptors = {
  1: ['middle'],
  2: ['lowest', 'highest'],
  3: ['lowest', 'middle', 'highest'],
  4: ['lowest', 'second-lowest', 'second-highest', 'highest'],
  5: ['lowest', 'second-lowest', 'middle', 'second-highest', 'highest'],
  6: ['lowest', 'second-lowest', 'third-lowest', 'third-hightest', 'second-highest', 'highest'],
};

const colors = {
  get orange() {
    return getCssProperty('--button-color') || config.Colors.orange;
  },
  get red() {
    return getCssProperty('--red-500-as-dark-fg') || config.Colors.red;
  },
  blueWhite: config.Colors.blueWhite,
  get green() {
    return getCssProperty('--app-theme-contrast') || config.Colors.green;
  },
};

export default class ProtoNotation {
  listeners: Function[] = [];

  SVG: SVGInteraction;

  svg: SVGSVGElement;

  /* From 0-100 */
  normalizedPoints: number[];

  disabled: boolean;

  points: { x: number; y: number }[];

  /* Highlights the next circle with the indicated color catching index by closure */
  private _highlighter: (color: string, index?: number) => void;

  circles: SVGCircleElement[];

  line: SVGPathElement;

  lines: SVGPathElement[];

  /* whether the dots are flashing left to right */
  private _flashing: boolean;

  /* the interval id controlling the flashing */
  private _intervalId: number;

  private eventDiv: HTMLDivElement;

  /* the index of the circle being dragged */
  private dragIndex: number | false = false;

  /* the index of the circle being moved via keyboard input */
  private focusIndex: number | false = false;

  private isDraggable: boolean = false;

  private sliders: { slider: HTMLSpanElement; setValue: (value: number) => void }[] | false;

  // Public constructor
  constructor(points: number[], userWidth?: number, userHeight?: number) {
    width = userWidth || width;
    height = userHeight || height;
    radius = Math.round((width + height) / (4 * 12));
    strokeWidth = radius / 2;
    this.init(points);
  }

  // Public Functions
  init(points: number[]) {
    const SVG = new SVGInteraction();
    this.SVG = SVG;
    this.svg = SVG.svg;
    SVG.resize(width, height);
    if (points) this.draw(points);
  }

  /**
   * @param points
   * @param doNotNormalize
   * @param {Array|string} points: array of points between 0 - 100
   */
  draw(points: string | number[], doNotNormalize = false, doNotUpdateTitle = false) {
    if (!doNotNormalize) points = normalize(points);
    else if (utils.isString(points)) {
      points = points.split('').map((c) => Number(c));
    }
    this.normalizedPoints = points;
    this.disabled = false;
    this.points = this.linePath(points);
    this.drawLine();
    this.drawCircles();
    if (typeof this._highlighter === 'undefined') {
      this.highlightNext(); // initializes!
    }
    if (!doNotUpdateTitle) this.updateTitle();
    // this.svg.disabled = false;
    return this;
  }

  updateTitle() {
    if (this.isDraggable) {
      this.updateSliders();
      return;
    }
    const zeroIndexedPoints = this.getAnswerArray();
    const pointsNeeded = zeroIndexedPoints.reduce((a, b) => Math.max(a, b)) + 1;
    // console.log('pointsNeeded', pointsNeeded);
    const heightWords = heightDescriptors[pointsNeeded];
    // console.log('heightWords', heightWords);
    const titleString = zeroIndexedPoints.map((point) => heightWords[point]).join(', ');
    this.SVG.setTitle(titleString);
  }

  updateSliders() {
    if (!this.sliders || this.sliders.length !== this.points.length) {
      this.createSliders();
    }
    if (!this.sliders) return;
    this.normalizedPoints.forEach((value, index) => {
      // console.log(`Slider ${index}: ${value}`);
      if (this.sliders) {
        this.sliders[index].setValue(value);
      }
    });
  }

  private createSliders() {
    const notationLabel = document.createElement('label');
    this.sliders = this.normalizedPoints.map((value, index) => {
      const slider = document.createElement('span');
      slider.innerHTML = `Note ${index + 1} Slider: ${value}`;
      slider.setAttribute('type', 'hidden');
      slider.setAttribute('aria-role', 'slider');
      slider.setAttribute('aria-valuemin', '0');
      slider.setAttribute('aria-valuemax', '100');
      slider.setAttribute('tabindex', '0');
      slider.setAttribute('aria-live', 'assertive');
      function setValue(newSliderValue: number) {
        slider.dataset.value = `${newSliderValue}`;
        slider.setAttribute('aria-valuenow', `${~~newSliderValue}`);
        slider.innerHTML = `Note ${index + 1} Slider: ${~~newSliderValue}`;
      }

      setValue(value);

      const mousetrap = Mousetrap(slider);
      mousetrap.bind(['up', 'right'], (kbdEvt) => {
        kbdEvt.preventDefault();
        const evt = document.createEvent('HTMLEvents');
        evt.initEvent('change', false, true);
        setValue(Number(slider.dataset.value) + 5);
        slider.dispatchEvent(evt);
      });
      mousetrap.bind(['down', 'left'], (kbdEvt) => {
        kbdEvt.preventDefault();
        const evt = document.createEvent('HTMLEvents');
        evt.initEvent('change', false, true);
        setValue(Number(slider.dataset.value) - 5);
        slider.dispatchEvent(evt);
      });

      slider.addEventListener('change', () => {
        let newValue = Number(slider.dataset.value);
        // console.log(`Raw newValue: ${newValue}`);
        const oldValue = this.normalizedPoints[index];
        // console.log(`Old value: ${oldValue}`);
        const difference = Math.abs(newValue - oldValue);
        // console.log(`Difference: ${difference}`);
        const direction = this.normalizedPoints[index] < newValue ? 1 : -1;
        if (difference < 5) {
          // console.log(`Adding ${5 * direction} to newValue`);
          newValue += 5 * direction;
        }
        const snappablePoint = this.normalizedPoints
          .filter((p, i) => {
            const isNotThePointBeingDragged = i !== index;
            const isCloseEnough = Math.abs(newValue - p) < 5;
            const hasNotAlreadyBeenSnappedToThis = oldValue !== p;
            // console.log(`\nConsidering snapping to ${i} === ${p}`);
            // console.log(`${i} isNotThePointBeingDragged`, isNotThePointBeingDragged);
            // console.log(`${i} isCloseEnough`, isCloseEnough);
            // console.log(`${i} hasNotAlreadyBeenSnapped`, hasNotAlreadyBeenSnappedToThis);
            return isNotThePointBeingDragged && isCloseEnough && hasNotAlreadyBeenSnappedToThis;
          })
          .sort((p1, p2) => Math.abs(p1 - newValue) - Math.abs(p2 - newValue))[0];
        if (snappablePoint) {
          setValue(snappablePoint);
          newValue = snappablePoint;
        }

        this.normalizedPoints[index] = newValue;
        this.draw(this.normalizedPoints, true, true);
      });

      slider.addEventListener('focus', () => {
        this.highlightCircle(index);
        this.focusIndex = index;
        this.dragIndex = false;
      });
      slider.addEventListener('focusout', () => {
        this.circles[index].style.fill = colors.orange;
      });

      notationLabel.appendChild(slider);
      return {
        slider,
        setValue,
      };
    });
    this.SVG.setLabel(notationLabel);
  }

  /*
   * Turn the drawing into a set of draggable points
   * for dictation-like exercises
   */
  makeDraggable() {
    this.isDraggable = true;
    this.appendEventDiv();
    this.activateDragListeners();
    this.addTransforms();
    this.styleForDragging();
    this.SVG.deleteTitle();
    this.SVG.deleteLabel();
  }

  /*
   * Reduces the dragged points to a question array (like [0,1,2,1]);
   *
   */
  getAnswerArray() {
    const comp = (a, b) => a - b;
    const arr = this.normalizedPoints;
    const set = new Set(arr);
    const a2 = Array.from(set).sort(comp);

    // If set size is 1, make sure that index comes out to 1:
    //      if(set.size === 1) a2 = [-9999999].concat(a2);
    return arr.map((n) => a2.indexOf(n));
  }

  /*
   * @param index: the index number of the circle to highlight
   * @param color: the color to highlight it (default red)
   *
   */
  highlightCircle(index, color?) {
    if (!this.circles) return this;
    color = color || colors.red;

    this.circles.forEach((circle, i) => {
      circle.style.fill = i === index ? color : colors.orange;
    });
    return this;
  }

  /*
   * Iterates through each circle, highlighting one after
   * another with each call of the function
   *
   * @param color: color to highlight the next note
   *
   */
  highlightNext(color?: string, index?: number) {
    if (typeof this._highlighter === 'undefined') {
      let i = 0;
      // eslint-disable-next-line @typescript-eslint/no-shadow
      this._highlighter = (color?, index?) => {
        i = typeof index === 'undefined' ? i : index;
        i %= this.circles.length;
        this.highlightCircle(i++, color);
      };
    } else {
      this._highlighter(color, index);
    }
  }

  /*
   * Disable
   * Greys out the image, disables mouse over, etc...
   *
   */
  disable() {
    this.stopFlashing();
    this.line.style.stroke = '#999999';
    this.circles.forEach((c) => {
      c.style.fill = '#999999';
    });
    this.disabled = true;
    this.SVG.svg.setAttribute('aria-disabled', 'true');
    // this.svg.disabled = true;
  }

  /*
   * Applies button styling (similar to .uButton) to
   * the SVG and makes it clickable.
   *
   * @param styleOverrides: CSS applied to make button
   * @param hoverOverrides: CSS applied for hover
   */

  styleAsButton(
    styleOverrides?: { [key: string]: string },
    hoverOverrides?: { [key: string]: string }
  ) {
    this.SVG.applyAttributes(this.svg, {
      'pointer-events': 'boundingBox',
    });

    const style = {
      display: 'inline-block',
      position: '',
      transform: '',
      borderRadius: '5px',
      background: colors.blueWhite,
      margin: '1.5%',
      border: 'none',
      boxShadow: '0px 7px 13px -1px rgba(0,0,0,0.4)',
      transition: 'box-shadow .1s ease, transform .1s ease',
      padding: '1%',
    };

    if (styleOverrides) {
      utils.merge(style, styleOverrides);
    }

    utils.merge(this.SVG.svg.style, style);
    const svg = this.SVG.svg;
    svg.setAttribute('aria-role', 'button');
    const hoverStyle = {
      position: 'relative',
      transform: 'translateY(-1px)',
      transition: 'box-shadow .1s ease, transform .1s ease',
      boxShadow: '0px 8px 14px 0px rgba(0,0,0,0.4)',
    };
    if (hoverOverrides) utils.merge(hoverStyle, hoverOverrides);

    const mouseover = (e) => {
      if (this.disabled) return;
      if (utils.isTouch()) return;
      this.startFlashing(250);
      e.preventDefault();
      utils.merge(svg.style, hoverStyle);
    };

    const mouseout = (e) => {
      if (this.disabled) return;
      if (utils.isTouch()) return;
      e.preventDefault();
      utils.merge(svg.style, style);
      this.stopFlashing();
    };

    svg.tabIndex = utils.uuid;
    svg.addEventListener('mouseover', mouseover.bind(this));
    svg.addEventListener('focus', mouseover.bind(this));
    svg.addEventListener('mouseout', mouseout.bind(this));
    svg.addEventListener('blur', mouseout.bind(this));
    svg.addEventListener('touchstart', this.click.bind(this));
    svg.addEventListener('mousedown', this.click.bind(this));
    svg.addEventListener('keyup', (e) => {
      const code = e.which;
      // 13 = Return, 32 = Space
      if (code === 13 || code === 32) {
        this.click(e);
      }
    });
    return this;
  }

  /*
   * startFlashing
   * @param delay (ms)
   */
  startFlashing(delay) {
    if (this.disabled) return;
    delay = delay || 325;
    if (!this._flashing) {
      this._flashing = true;
      this.highlightNext(undefined, 0);
      this._intervalId = window.setInterval(() => this.highlightNext(), delay);
    }
  }

  /*
   * stopFlashing
   */
  stopFlashing() {
    this._flashing = false;
    this.highlightNext();
    if (this._intervalId) {
      window.clearInterval(this._intervalId);
    }
    this.highlightCircle(-1);
  }

  /*
   * add callback when SVG is clicked or activated from focus
   * only effective if styleAsButton() has been run.
   */
  onclick(callback: Function, thisArg: any) {
    if (thisArg) {
      this.listeners.push(callback.bind(thisArg));
    } else {
      this.listeners.push(callback);
    }
    return this;
  }

  resizeOverlay() {
    const rect = this.svg.getBoundingClientRect();
    const parentRect = (this.svg.parentNode as HTMLDivElement).getBoundingClientRect();

    const w = rect.right - rect.left;
    const h = rect.bottom - rect.top;
    const top = rect.top - parentRect.top;
    const left = rect.left - parentRect.left;

    const style = {
      position: 'absolute',
      zIndex: 32,
      top: `${top}px`,
      left: `${left}px`,
      width: `${w}px`,
      height: `${h}px`,
    };
    utils.merge(this.eventDiv.style, style);
  }

  exportSVG() {
    const s = new window.XMLSerializer();
    return s.serializeToString(this.svg);
  }

  /*
   * ===== Internal Use Functions ====
   */
  private linePath(points: number[]): { x: number; y: number }[] {
    const svg = this.SVG;
    const offset = radius;
    const lHeight = height - 2 * radius;
    const lWidth = width - 2 * radius;
    return points.map((point, index) => {
      const y = height - radius - lHeight * (point / 100);
      const x = lWidth * (index / (points.length - 1)) + offset;
      if (index === 0) {
        svg.beginPath().moveTo(x, y);
      } else {
        svg.lineTo(x, y);
      }
      return { x, y };
    });
  }

  private dashedLines() {
    if (!this.lines) this.lines = [];
    else if (this.lines.length > this.points.length) {
      for (let i = this.points.length; i < this.lines.length; i++) {
        this.SVG.svg.removeChild(this.lines[i]);
      }
      this.lines.length = this.points.length;
    }

    this.points.forEach((point, index) => {
      const y = point.y;
      this.SVG.save();
      this.SVG.setLineDash([radius * 1.25, radius * 2]);
      this.SVG.beginPath();
      this.SVG.moveTo(0, y).lineTo(width, y);

      // Does it exist? Reposition it:
      if (this.lines[index]) {
        this.SVG.applyAttributes(this.lines[index], { d: this.SVG.path });
      } else {
        this.SVG.stroke({
          stroke: '#999999',
          'stroke-width': 1,
        });
        this.lines.push(this.SVG.lastChild as SVGPathElement);
        if (this.line) {
          this.SVG.svg.removeChild(this.SVG.lastChild);
          this.SVG.svg.insertBefore(this.SVG.lastChild, this.line);
        }
      }
      this.SVG.restore();
    });
  }

  private drawLine() {
    this.dashedLines();
    if (this.line) {
      this.SVG.applyAttributes(this.line, {
        d: this.SVG.path,
      });
      this.line.style.stroke = colors.green;
    } else {
      this.SVG.stroke({
        stroke: colors.green,
        'stroke-width': strokeWidth,
      });
      this.line = this.SVG.lastChild as SVGPathElement;
    }
  }

  private drawCircles() {
    if (!this.circles || this.circles.length !== this.points.length) {
      this.createCircles();
    }

    this.points.forEach((point, index) => {
      const circle = this.circles[index];
      this.SVG.applyAttributes(circle, { cx: point.x, cy: point.y });
      if (this.dragIndex !== index && this.focusIndex !== index) {
        circle.style.fill = colors.orange;
      }
    });
  }

  private createCircles() {
    if (this.circles) {
      this.circles.forEach((circle) => {
        this.SVG.svg.removeChild(circle);
      });
    }
    this.circles = [];
    this.points.forEach((point) => {
      this.SVG.circle(point.x, point.y, radius, {
        stroke: 'none',
        fill: colors.orange,
      });
      this.SVG.lastChild.style.transition = 'fill .1s ease-out';
      this.circles.push(this.SVG.lastChild as SVGCircleElement);
    });
  }

  private click(e) {
    if (e.target.blur) e.target.blur();
    if (this.disabled) return;
    e.preventDefault();
    this.listeners.forEach((callback) => {
      callback();
    });
  }

  /*
   * ========= Draggable Helper Functions ==========
   *
   */

  private drag(e) {
    if (this.dragIndex === false) return;
    e.preventDefault();
    const coords = this.coords(e);
    this.normalizedPoints.some((p, i) => {
      if (i !== this.dragIndex && Math.abs(p - coords.y) <= 5) {
        coords.y = p;
        return true;
      }
      return false;
    });

    this.normalizedPoints[this.dragIndex] = coords.y;
    this.draw(this.normalizedPoints, true);
  }

  private dragStart(e) {
    e.preventDefault();
    const coords = this.coords(e);
    this.dragIndex = coords.x;
    this.focusIndex = coords.x;
    console.log('coords', coords);
    const slider = this.sliders[coords.x];
    console.log('slider', slider);
    utils.merge(this.circles[this.dragIndex].style, {
      fill: colors.red,
      r: radius * 1.25,
    });
    slider.slider.focus();
    this.dragIndex = coords.x;
  }

  private dragEnd(e) {
    if (this.dragIndex !== false) {
      e.preventDefault();
      utils.merge(this.circles[this.dragIndex].style, {
        // fill: colors.orange, because it's still movable via kbd
        r: radius,
      });
    }
    this.dragIndex = false;
  }

  private coords(e) {
    if ('touches' in e) {
      e = e.touches[0];
    }
    const rect = this.svg.getBoundingClientRect();
    const dragY = (100 * (rect.bottom - e.clientY)) / (rect.bottom - rect.top);
    const dragX =
      ((this.normalizedPoints.length - 1) * (e.clientX - rect.left)) / (rect.right - rect.left);
    const normalizedX = Math.round(dragX);
    let normalizedY = (10 * dragY - 50) / 9;
    normalizedY = Math.max(0, normalizedY);
    normalizedY = Math.min(100, normalizedY);
    return { x: normalizedX, y: normalizedY };
  }

  private appendEventDiv() {
    const rect = this.svg.getBoundingClientRect();
    const div = this.svg.parentNode;
    const eventDiv = document.createElement('div');
    const w = rect.right - rect.left;
    const h = rect.bottom - rect.top;
    const style = {
      position: 'absolute',
      zIndex: 32,
      top: rect.top,
      left: rect.left,
      width: `${w}px`,
      height: `${h}px`,
    };
    utils.merge(eventDiv.style, style);
    div.appendChild(eventDiv);

    this.eventDiv = eventDiv;
  }

  private activateDragListeners() {
    const div = this.eventDiv;
    [
      ['mousedown', this.dragStart],
      ['touchstart', this.dragStart],
      ['mouseout', this.dragEnd],
      ['touchcancel', this.dragEnd],
      ['mouseup', this.dragEnd],
      ['touchend', this.dragEnd],
      ['mousemove', this.drag],
      ['touchmove', this.drag],
    ].forEach(([type, callback]) => {
      div.addEventListener(type as any, (callback as any).bind(this));
    });
  }

  private addTransforms() {
    this.circles.forEach((c) => {
      c.style.transition = 'fill 0.15s ease-out, r 0.15s ease-out';
    });
  }

  private styleForDragging() {
    const style = {
      display: 'inline-block',
      position: '',
      transform: '',
      borderRadius: '5px',
      background: colors.blueWhite,
      margin: '1.5%',
      border: 'none',
      boxShadow: '0px 7px 13px -1px rgba(0,0,0,0.4)',
      padding: '1%',
    };
    utils.merge(this.svg.style, style);
  }
}

/*
 * @param stringOrArray: an array of numbers, or a string of ints
 *   to be normalized onto a 100-point array
 */
function normalize(stringOrArray: string | number[]): number[] {
  const comp = (a, b) => a - b;

  let arr: number[];
  if (utils.isString(stringOrArray)) {
    arr = stringOrArray.split('').map((n) => Number(n));
  } else {
    arr = stringOrArray;
  }

  let max = arr.slice(0).sort(comp).slice(-1)[0];
  const set = new Set(arr);
  if (set.size === 1) {
    max = 2;
    arr = '1'
      .repeat(arr.length)
      .split('')
      .map((c) => Number(c));
  }

  return arr.map((n) => Math.round(100 * (n / max)));
}
